import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useApiAuthed, useApiDev } from "../auth.js";
import { loadGeoSelection, type GeoSelection } from "../components/GeoSelector.js";
import type {
  AccountabilityActor,
  GapDetail,
  NearImpactItem,
  OverviewInsight,
  OverviewModuleView,
  OverviewResponse,
} from "../api.js";

interface PageProps {
  devModeNoAuth?: boolean;
}

type IntelSectionKey = "system" | "capital" | "policy" | "legal" | "workforce" | "media" | "education";

interface IntelEvidence {
  id: string;
  title: string;
  detail: string;
  url?: string | null;
}

interface IntelSection {
  key: IntelSectionKey;
  title: string;
  summary: string;
  status: string;
  urgency: string;
  entities: AccountabilityActor[];
  gaps: GapDetail[];
  evidence: IntelEvidence[];
}

const SECTION_ORDER: IntelSectionKey[] = [
  "system",
  "capital",
  "policy",
  "legal",
  "workforce",
  "media",
  "education",
];

const SECTION_COPY: Record<IntelSectionKey, { title: string; fallback: string }> = {
  system: {
    title: "System State",
    fallback: "Read the system pattern across capital, policy, legal, workforce, media, and education.",
  },
  capital: {
    title: "Capital",
    fallback: "Trace where money is flowing and where it is not reaching Black communities.",
  },
  policy: {
    title: "Policy",
    fallback: "Separate policy producing outcomes from policy performing progress.",
  },
  legal: {
    title: "Legal",
    fallback: "Track enforcement, rights, court decisions, and justice outcomes affecting Black communities.",
  },
  workforce: {
    title: "Workforce",
    fallback: "Understand hiring, wages, placement, and near-impact opportunities.",
  },
  media: {
    title: "Media",
    fallback: "Compare narrative amplification against measurable outcomes.",
  },
  education: {
    title: "Education",
    fallback: "Track outcome gaps and institutional movement.",
  },
};

export function IntelPage({ devModeNoAuth }: PageProps) {
  const api = devModeNoAuth ? useApiDev() : useApiAuthed();
  const [geo] = useState<GeoSelection>(() => loadGeoSelection());
  const [data, setData] = useState<OverviewResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setError(null);
    api
      .overview("7d", geo)
      .then((overview) => {
        if (alive) setData(overview);
      })
      .catch((err) => {
        if (alive) setError(String(err));
      });
    return () => {
      alive = false;
    };
  }, [api, geo]);

  const sections = useMemo(() => buildSections(data), [data]);
  const whyCards = useMemo(() => buildWhyCards(data), [data]);

  return (
    <div className="bios-intel-page">
      <header className="bios-intel-header">
        <div>
          <div className="bios-intel-kicker">Intel</div>
          <p>See what is shaping outcomes.</p>
        </div>
        {geo.scope !== "national" && <span className="bios-intel-geo">{geoLabel(geo)}</span>}
      </header>

      {error && <div className="bios-intel-state bios-intel-error">{error}</div>}
      {!error && !data && <div className="bios-intel-state">Reading the system...</div>}

      {whyCards.length > 0 && (
        <section className="bios-intel-why" aria-label="Understand why">
          {whyCards.map((card) => (
            <a key={card.href} href={card.href} className="bios-intel-why-card">
              <span>Understand why</span>
              <strong>{card.title}</strong>
            </a>
          ))}
        </section>
      )}

      <section className="bios-intel-sections" aria-label="Intel sections">
        {sections.map((section, index) => (
          <details
            key={section.key}
            id={`intel-${section.key}`}
            className="bios-intel-section"
          >
            <summary>
              <span className="bios-intel-section-name">{section.title}</span>
              <span className="bios-intel-section-summary">{section.summary}</span>
              <span className="bios-intel-section-meta">
                <span>{section.status}</span>
                <span>{section.urgency}</span>
              </span>
            </summary>
            <div className="bios-intel-section-body">
              <IntelColumn title="Entities" empty="No driving actors isolated yet.">
                {section.entities.slice(0, 3).map((entity, i) => (
                  <div key={`${entity.name}-${i}`} className="bios-intel-minirow">
                    <strong>{entity.name}</strong>
                    <span>{entity.involvement}</span>
                  </div>
                ))}
              </IntelColumn>
              <IntelColumn title="Gaps" empty="No priority gaps surfaced.">
                {section.gaps.slice(0, 3).map((gap) => (
                  <div key={`${section.key}-${gap.kind}-${gap.what}`} className="bios-intel-minirow">
                    <strong>{gap.what}</strong>
                    <span>{gap.why}</span>
                  </div>
                ))}
              </IntelColumn>
              <IntelColumn title="Evidence" empty="No evidence items in this window.">
                {section.evidence.slice(0, 3).map((item) => (
                  <div key={item.id} className="bios-intel-minirow">
                    <strong>
                      {item.url ? (
                        <a href={item.url} target="_blank" rel="noreferrer">
                          {item.title}
                        </a>
                      ) : (
                        item.title
                      )}
                    </strong>
                    <span>{item.detail}</span>
                  </div>
                ))}
              </IntelColumn>
            </div>
          </details>
        ))}
      </section>

      <IntelBottomNav active="intel" />
      <div className="bios-feed-bottomnav-spacer" aria-hidden />
    </div>
  );
}

function IntelColumn({
  title,
  empty,
  children,
}: {
  title: string;
  empty: string;
  children: ReactNode[];
}) {
  const hasChildren = children.filter(Boolean).length > 0;
  return (
    <div className="bios-intel-column">
      <div className="bios-intel-column-title">{title}</div>
      {hasChildren ? children : <div className="bios-intel-empty">{empty}</div>}
    </div>
  );
}

function buildSections(data: OverviewResponse | null): IntelSection[] {
  return SECTION_ORDER.map((key) => buildSection(key, data));
}

function buildSection(key: IntelSectionKey, data: OverviewResponse | null): IntelSection {
  const moduleView = data?.modules.find((m) => m.module === key);
  const gaps = key === "system"
    ? data?.gap_summary?.by_kind ?? []
    : (data?.gap_summary?.by_kind ?? []).filter((g) => g.modules.includes(key));
  const entities = key === "system"
    ? data?.accountability?.actors ?? []
    : (data?.accountability?.actors ?? []).filter((a) => a.module === key);
  const evidence = key === "system"
    ? evidenceFromOverview(data)
    : evidenceFromOverview(data).filter((item) => item.id.includes(`:${key}:`));

  return {
    key,
    title: SECTION_COPY[key].title,
    summary: sectionSummary(key, data, moduleView),
    status: sectionStatus(key, data, moduleView),
    urgency: key === "system" ? data?.urgency?.level ?? "low" : moduleView?.status ?? "quiet",
    entities,
    gaps,
    evidence,
  };
}

function sectionSummary(
  key: IntelSectionKey,
  data: OverviewResponse | null,
  moduleView?: OverviewModuleView,
): string {
  if (!data) return SECTION_COPY[key].fallback;
  if (key === "system") {
    return data.interpretation?.headline ?? data.headline_truth ?? SECTION_COPY.system.fallback;
  }
  const rec = data.focus_recommendations?.find((r) => r.module === key);
  const gap = data.gap_summary?.by_kind.find((g) => g.modules.includes(key));
  return rec?.reason ?? gap?.what ?? moduleView?.status.replace(/-/g, " ") ?? SECTION_COPY[key].fallback;
}

function sectionStatus(
  key: IntelSectionKey,
  data: OverviewResponse | null,
  moduleView?: OverviewModuleView,
): string {
  if (!data) return "pending";
  if (key === "system") {
    const measurable = data.system_impact.measurable_black_items;
    return `${measurable} measurable impact items`;
  }
  if (!moduleView) return "quiet";
  return `${Math.round(moduleView.reality_pct)}% reality`;
}

function evidenceFromOverview(data: OverviewResponse | null): IntelEvidence[] {
  if (!data) return [];
  const insights = (data.top_insights ?? []).map((item: OverviewInsight) => ({
    id: `insight:${item.module}:${item.id}`,
    title: item.title,
    detail: item.why_matters || item.what_happened,
    url: item.url,
  }));
  const near = (data.near_impact ?? []).map((item: NearImpactItem) => ({
    id: `near:${item.module}:${item.id}`,
    title: item.title,
    detail: [item.population_impact, item.impact_state, item.impact_proximity].filter(Boolean).join(" · "),
    url: item.url,
  }));
  return [...insights, ...near];
}

function buildWhyCards(data: OverviewResponse | null): Array<{ title: string; href: string }> {
  if (!data) return [];
  return (data.gap_summary?.by_kind ?? []).slice(0, 3).map((gap) => ({
    title: gap.what,
    href: `#intel-${(gap.modules[0] as IntelSectionKey | undefined) ?? "system"}`,
  }));
}

function IntelBottomNav({
  active,
}: {
  active: "signals" | "command" | "intel" | "search" | "profile";
}) {
  return (
    <nav className="bios-feed-bottomnav bios-intel-bottomnav" aria-label="Primary navigation">
      <Link to="/app" className={`bios-feed-bottomnav-tab${active === "signals" ? " active" : ""}`}>
        <span className="bios-feed-bottomnav-icon" aria-hidden>◎</span>
        <span className="bios-feed-bottomnav-label">Signals</span>
      </Link>
      <Link to="/app/command" className={`bios-feed-bottomnav-tab${active === "command" ? " active" : ""}`}>
        <span className="bios-feed-bottomnav-icon" aria-hidden>⌘</span>
        <span className="bios-feed-bottomnav-label">Command</span>
      </Link>
      <Link to="/app/intel" className={`bios-feed-bottomnav-tab${active === "intel" ? " active" : ""}`}>
        <span className="bios-feed-bottomnav-icon" aria-hidden>◐</span>
        <span className="bios-feed-bottomnav-label">Intel</span>
      </Link>
      <Link to="/app/search" className={`bios-feed-bottomnav-tab${active === "search" ? " active" : ""}`}>
        <span className="bios-feed-bottomnav-icon" aria-hidden>⌕</span>
        <span className="bios-feed-bottomnav-label">Search</span>
      </Link>
      <Link to="/app/profile" className={`bios-feed-bottomnav-tab${active === "profile" ? " active" : ""}`}>
        <span className="bios-feed-bottomnav-icon" aria-hidden>◌</span>
        <span className="bios-feed-bottomnav-label">Profile</span>
      </Link>
    </nav>
  );
}

const STATE_LABELS: Record<string, string> = {
  AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California",
  CO: "Colorado", CT: "Connecticut", DC: "District of Columbia", DE: "Delaware",
  FL: "Florida", GA: "Georgia", HI: "Hawaii", ID: "Idaho", IL: "Illinois",
  IN: "Indiana", IA: "Iowa", KS: "Kansas", KY: "Kentucky", LA: "Louisiana",
  ME: "Maine", MD: "Maryland", MA: "Massachusetts", MI: "Michigan",
  MN: "Minnesota", MS: "Mississippi", MO: "Missouri", MT: "Montana",
  NE: "Nebraska", NV: "Nevada", NH: "New Hampshire", NJ: "New Jersey",
  NM: "New Mexico", NY: "New York", NC: "North Carolina", ND: "North Dakota",
  OH: "Ohio", OK: "Oklahoma", OR: "Oregon", PA: "Pennsylvania",
  RI: "Rhode Island", SC: "South Carolina", SD: "South Dakota",
  TN: "Tennessee", TX: "Texas", UT: "Utah", VT: "Vermont", VA: "Virginia",
  WA: "Washington", WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming",
};

function geoLabel(geo: GeoSelection): string {
  if (geo.scope === "city" && geo.city) return geo.city;
  if (geo.scope === "state" && geo.state) return STATE_LABELS[geo.state] ?? geo.state;
  return "National";
}
